import { parsePhoneNumberFromString } from 'libphonenumber-js';

export type RegistrationSessionValues = {
  email: string;
  firstName: string;
  lastName: string;
  instagram: string;
  phoneCountryDialCode: string;
  phoneNumber: string;
};

export type UnregisteredUser = {
  email: string;
  firstName: string;
  lastName: string;
  instagram: string;
  phone: string | null;
};

export const REGISTRATION_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const REGISTRATION_PHONE_NUMBER_REGEX = /^\+\d{1,4} \d{4,14}$/;

export const normalizeRegistrationString = (value: string): string =>
  value.trim();

export const normalizeRegistrationEmail = (email: string): string =>
  normalizeRegistrationString(email).toLowerCase();

export const isRegistrationEmail = (email: string): boolean => {
  const normalizedEmail = normalizeRegistrationEmail(email);
  return REGISTRATION_EMAIL_REGEX.test(normalizedEmail);
};

export const normalizeRegistrationInstagram = (instagram: string): string =>
  normalizeRegistrationString(instagram).replace(/^@/, '').toLowerCase();

const normalizeDialCode = (dialCode: string): string => {
  const onlyDigits = dialCode.replace(/\D/g, '');
  if (!onlyDigits) {
    return '+1';
  }

  return `+${onlyDigits}`;
};

export const buildRegistrationPhoneNumber = (
  dialCode: string,
  number: string,
): string | null => {
  const onlyDigits = number.replace(/\D/g, '');
  if (!onlyDigits) {
    return null;
  }

  const normalizedDialCode = normalizeDialCode(dialCode);
  const fullNumber = `${normalizedDialCode}${onlyDigits}`;
  const parsed = parsePhoneNumberFromString(fullNumber);
  if (!parsed || !parsed.isValid()) {
    return null;
  }

  if (parsed.nationalNumber !== onlyDigits) {
    return null;
  }

  return `${normalizedDialCode} ${onlyDigits}`;
};

export const toUnregisteredUser = (
  values: RegistrationSessionValues,
): UnregisteredUser => {
  return {
    email: normalizeRegistrationEmail(values.email),
    firstName: normalizeRegistrationString(values.firstName),
    lastName: normalizeRegistrationString(values.lastName),
    instagram: normalizeRegistrationInstagram(values.instagram),
    phone: buildRegistrationPhoneNumber(
      values.phoneCountryDialCode,
      values.phoneNumber,
    ),
  };
};

export type DashboardRedirectDecision = 'none' | 'home';

type ResolveDashboardRedirectInput = {
  isLoggedIn: boolean;
};

export const resolveDashboardRedirect = ({
  isLoggedIn,
}: ResolveDashboardRedirectInput): DashboardRedirectDecision => {
  if (isLoggedIn) {
    return 'home';
  }

  return 'none';
};

export * from './registration.steps.model';
